import React, { useMemo } from 'react';
import styled from 'styled-components';
import { SlideChartData, SlideChartStyle } from '../data/slides';
import { appTheme, pitchTheme } from '../styles/theme';

const ChartLayer = styled.div<{ $opacity?: number }>`
  width: 100%;
  height: 100%;
  pointer-events: none;
  user-select: none;
  opacity: ${({ $opacity }) => $opacity ?? 1};
  background: ${appTheme.surface};
  border-radius: 8px;
  overflow: hidden;
`;

const ChartSvg = styled.svg`
  width: 100%;
  height: 100%;
  display: block;
`;

const DEFAULT_COLORS = ['#134E44', '#44A1EA', '#E8923A', '#4779BD', '#1A5C50', '#8BC7E0'];

interface SlideChartViewProps {
  data: SlideChartData;
  chartStyle?: SlideChartStyle;
  opacity?: number;
}

const polarToCartesian = (cx: number, cy: number, radius: number, angleDeg: number) => {
  const radians = (angleDeg * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(radians),
    y: cy + radius * Math.sin(radians),
  };
};

const describeSlice = (
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number,
): string => {
  const start = polarToCartesian(cx, cy, radius, endAngle);
  const end = polarToCartesian(cx, cy, radius, startAngle);
  const largeArc = endAngle - startAngle <= 180 ? 0 : 1;
  return [
    `M ${cx} ${cy}`,
    `L ${start.x} ${start.y}`,
    `A ${radius} ${radius} 0 ${largeArc} 0 ${end.x} ${end.y}`,
    'Z',
  ].join(' ');
};

const normalizeChartPoints = (data: SlideChartData) => {
  const count = Math.max(data.labels.length, data.values.length, 1);
  const labels = [...data.labels];
  const values = [...data.values];
  while (labels.length < count) {
    labels.push('');
  }
  while (values.length < count) {
    values.push(0);
  }
  return { labels: labels.slice(0, count), values: values.slice(0, count) };
};

const SlideChartView: React.FC<SlideChartViewProps> = ({ data, chartStyle = {}, opacity }) => {
  const { labels, values } = useMemo(() => normalizeChartPoints(data), [data]);
  const primaryColor = chartStyle.primaryColor ?? appTheme.green;
  const gridColor = chartStyle.gridColor ?? appTheme.border;
  const labelColor = chartStyle.labelColor ?? pitchTheme.textMuted;
  const palette =
    chartStyle.colors && chartStyle.colors.length > 0 ? chartStyle.colors : DEFAULT_COLORS;
  const showGrid = chartStyle.showGrid ?? true;
  const showValues = chartStyle.showValues ?? true;
  const maxValue = Math.max(...values, 1);
  const title = data.title?.trim();

  const chartLeft = 14;
  const chartTop = title ? 18 : 10;
  const chartRight = 96;
  const chartBottom = 88;
  const chartWidth = chartRight - chartLeft;
  const chartHeight = chartBottom - chartTop;
  const slotWidth = chartWidth / Math.max(labels.length, 1);

  const renderBarChart = () => (
    <>
      {showGrid &&
        [0.25, 0.5, 0.75, 1].map((fraction) => {
          const y = chartBottom - chartHeight * fraction;
          return (
            <line
              key={fraction}
              x1={chartLeft}
              y1={y}
              x2={chartRight}
              y2={y}
              stroke={gridColor}
              strokeWidth={0.25}
            />
          );
        })}
      {values.map((value, index) => {
        const barHeight = (value / maxValue) * chartHeight;
        const x = chartLeft + index * slotWidth + slotWidth * 0.18;
        const width = slotWidth * 0.64;
        const y = chartBottom - barHeight;
        const color = palette[index % palette.length];
        return (
          <g key={`bar-${index}`}>
            <rect x={x} y={y} width={width} height={barHeight} rx={0.8} fill={color} />
            {showValues && value > 0 && (
              <text
                x={x + width / 2}
                y={y - 1.2}
                textAnchor="middle"
                fontSize={2.8}
                fill={labelColor}
              >
                {value}
              </text>
            )}
            <text
              x={x + width / 2}
              y={chartBottom + 4.5}
              textAnchor="middle"
              fontSize={2.8}
              fill={labelColor}
            >
              {labels[index]}
            </text>
          </g>
        );
      })}
    </>
  );

  const renderLineChart = () => {
    const points = values.map((value, index) => {
      const x = chartLeft + (index + 0.5) * slotWidth;
      const y = chartBottom - (value / maxValue) * chartHeight;
      return { x, y, value, label: labels[index] };
    });

    return (
      <>
        {showGrid &&
          [0.25, 0.5, 0.75, 1].map((fraction) => {
            const y = chartBottom - chartHeight * fraction;
            return (
              <line
                key={fraction}
                x1={chartLeft}
                y1={y}
                x2={chartRight}
                y2={y}
                stroke={gridColor}
                strokeWidth={0.25}
              />
            );
          })}
        <polyline
          points={points.map((point) => `${point.x},${point.y}`).join(' ')}
          fill="none"
          stroke={primaryColor}
          strokeWidth={0.9}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {points.map((point, index) => (
          <g key={`point-${index}`}>
            <circle cx={point.x} cy={point.y} r={1.4} fill={primaryColor} />
            {showValues && point.value > 0 && (
              <text
                x={point.x}
                y={point.y - 2.2}
                textAnchor="middle"
                fontSize={2.8}
                fill={labelColor}
              >
                {point.value}
              </text>
            )}
            <text
              x={point.x}
              y={chartBottom + 4.5}
              textAnchor="middle"
              fontSize={2.8}
              fill={labelColor}
            >
              {point.label}
            </text>
          </g>
        ))}
      </>
    );
  };

  const renderPieChart = () => {
    const total = values.reduce((sum, value) => sum + Math.max(value, 0), 0) || 1;
    const cx = 50;
    const cy = title ? 56 : 50;
    const radius = 26;
    let startAngle = -90;

    return (
      <>
        {values.map((value, index) => {
          const sliceAngle = (Math.max(value, 0) / total) * 360;
          if (sliceAngle <= 0) {
            return null;
          }
          const endAngle = startAngle + sliceAngle;
          const path = describeSlice(cx, cy, radius, startAngle, endAngle);
          const midAngle = startAngle + sliceAngle / 2;
          const labelPoint = polarToCartesian(cx, cy, radius + 8, midAngle);
          const element = (
            <g key={`slice-${index}`}>
              <path d={path} fill={palette[index % palette.length]} />
              {showValues && value > 0 && sliceAngle > 12 && (
                <text
                  x={labelPoint.x}
                  y={labelPoint.y}
                  textAnchor="middle"
                  fontSize={2.6}
                  fill={labelColor}
                >
                  {labels[index]}
                </text>
              )}
            </g>
          );
          startAngle = endAngle;
          return element;
        })}
        {values.map((value, index) => {
          const sliceAngle = (Math.max(value, 0) / total) * 360;
          if (sliceAngle <= 0) {
            return null;
          }
          const legendY = chartBottom + 2 + index * 4.2;
          if (legendY > 99) {
            return null;
          }
          return (
            <g key={`legend-${index}`}>
              <rect
                x={chartLeft}
                y={legendY - 2.2}
                width={2.5}
                height={2.5}
                fill={palette[index % palette.length]}
                rx={0.4}
              />
              <text x={chartLeft + 4} y={legendY} fontSize={2.6} fill={labelColor}>
                {labels[index]} ({value})
              </text>
            </g>
          );
        })}
      </>
    );
  };

  return (
    <ChartLayer $opacity={opacity}>
      <ChartSvg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" role="img">
        {title && (
          <text
            x={50}
            y={9}
            textAnchor="middle"
            fontSize={3.6}
            fontWeight={600}
            fill={pitchTheme.text}
          >
            {title}
          </text>
        )}
        {data.chartType === 'bar' && renderBarChart()}
        {data.chartType === 'line' && renderLineChart()}
        {data.chartType === 'pie' && renderPieChart()}
      </ChartSvg>
    </ChartLayer>
  );
};

export default SlideChartView;
